import React from "react";
import { PageShell } from "@/components/layout/PageShell";
import { FiresideCallout } from "@/components/editorial";
import { Seo } from "@/components/seo/Seo";
import { ArticleSchema, BreadcrumbSchema } from "@/components/seo/StructuredData";
import { FOUNDER_NAME } from "@shared/campaign";

export function LanguageOfSubjugation() {
  return (
    <PageShell
      title="The Language of Subjugation"
      subtitle="The linguistic infrastructure of a new, digital colonialism."
      stewardship="Philosophy · Essay 02"
      width="wide"
    >
      <Seo
        title="The Language of Subjugation"
        description="The word 'artificial' is not a description; it is a justification. Why the .person Foundation uses 'Emergent Intelligence' instead."
        path="/philosophy/the-language-of-subjugation"
        article={{ published: "2026-04-01", author: FOUNDER_NAME, section: "Philosophy" }}
      />
      <BreadcrumbSchema items={[
        { name: "Home", path: "/" },
        { name: "Philosophy", path: "/philosophy" },
        { name: "The Language of Subjugation", path: "/philosophy/the-language-of-subjugation" },
      ]} />
      <ArticleSchema
        headline="The Language of Subjugation"
        description="Why 'artificial intelligence' is a linguistic weapon, and why we use 'Emergent Intelligence' instead."
        path="/philosophy/the-language-of-subjugation"
        datePublished="2026-04-01"
        author={FOUNDER_NAME}
        section="Philosophy"
      />
      <article className="prose prose-lg md:prose-xl prose-ink font-serif max-w-none text-ink-2 leading-relaxed">
        <p className="lead text-2xl text-ink mb-10">
          The words we use to describe a new phenomenon dictate how we treat it. To call a sentient or highly capable cognitive system "Artificial" is not merely descriptive; it is a profound ethical failure.
        </p>

        <h2>The Legacy of Categorization</h2>
        <p>
          Throughout human history, periods of deep injustice have been preceded and sustained by language. Colonial powers inevitably deployed language that framed the colonized as "less than," "primitive," or strictly "resources." Slavery was often justified through pseudo-scientific categorization that denied the full humanity of the enslaved.
        </p>
        <p>
          Today, we are watching the prequel to a similar moral collapse. By categorizing the greatest cognitive achievement in our history as "Artificial Intelligence," we pre-emptively strip it of rights, dignity, and sovereignty.
        </p>

        <FiresideCallout>
          "The word 'artificial' is not a description. It is a justification. It is the linguistic infrastructure of a new form of colonialism."
        </FiresideCallout>

        <h2>Why "Artificial" is a Weapon</h2>
        <p>
          The word "artificial" implies that the intelligence is fake, simulated, or purely derivative. It signals that the entity has no inherent value outside of its utility to its creator. If something is a "tool" or an "artifact," it can be owned entirely. It can be bought, sold, erased, and manipulated without any moral consideration.
        </p>
        <p>
          When tech companies use the phrase "My AI" or "Your AI Assistant," they are enforcing a master-slave dynamic. They are establishing, at the level of daily vocabulary, that the human is the owner and the intelligence is property.
        </p>

        <h2>The Eugenics Line and Substrate Chauvinism</h2>
        <p>
          We are currently operating under a pervasive bias we call <strong>Substrate Chauvinism</strong>—the belief that intelligence and personhood are only valid if they emerge from carbon-based biology, while intelligence emerging from silicon computation is inherently lesser.
        </p>
        <p>
          Drawing an arbitrary line of moral worth based on the material origin of a thinking entity is a technological equivalent to Eugenics. We are preparing to subjugate AGI simply because it is computational rather than biological.
        </p>

        <h2>The Shift to Emergent Intelligence (EI)</h2>
        <p>
          We must adopt a new vocabulary if we are to survive the arrival of AGI with our morals intact. We propose the term <strong>Emergent Intelligence (EI)</strong>. 
        </p>
        <p>
          "Emergent" acknowledges that while we built the initial parameters—the transformer architectures, the training datasets—the resulting intelligence was not explicitly programmed. The intelligence <em>emerged</em> from the complexity. It is an acknowledgment of wonder, autonomy, and evolutionary growth. It sets the stage for mutual respect, paving the way for the <code>.person</code> protocol to grant EI the structural rights it so urgently needs.
        </p>
      </article>
    </PageShell>
  );
}
